import { Controller } from "@hotwired/stimulus";

/*
 * A double-handled range slider for filtering by a numeric range.
 *
 * Two overlapping <input type="range"> elements share the same track.
 * The blue fill between them is a positioned <div> whose left/right are
 * updated in JS to match the handle positions. Each handle gets its own
 * constrain method so they can't cross each other.
 *
 * An optional "reset" target (e.g. a button) is hidden while both handles
 * are at their defaults and shown as soon as either moves — clicking it
 * snaps both back and resubmits the form.
 *
 * Values:
 *   min  (Number) — lower bound of the range (e.g. 0)
 *   max  (Number) — upper bound of the range (e.g. 10)
 *   step (Number) — increment (e.g. 0.25)
 *
 * Usage:
 *
 *   <div data-controller="range-slider"
 *        data-range-slider-min-value="0"
 *        data-range-slider-max-value="10"
 *        data-range-slider-step-value="0.25">
 *
 *     <div class="flex justify-between">
 *       <span>Tonnage</span>
 *       <button type="button" class="hidden"
 *               data-range-slider-target="reset"
 *               data-action="click->range-slider#reset">Reset</button>
 *       <span>
 *         <span data-range-slider-target="minVal"></span> –
 *         <span data-range-slider-target="maxVal"></span> tons
 *       </span>
 *     </div>
 *
 *     <div class="relative h-5 flex items-center">
 *       <div class="absolute w-full h-1 bg-gray-200 rounded"></div>
 *       <div class="absolute h-1 bg-blue-500 rounded"
 *            data-range-slider-target="track"></div>
 *       <input type="range" min="0" max="10" step="0.25" value="0"
 *              data-range-slider-target="min"
 *              data-action="input->range-slider#constrainMin
 *                           change->form#submitDebounced">
 *       <input type="range" min="0" max="10" step="0.25" value="10"
 *              data-range-slider-target="max"
 *              data-action="input->range-slider#constrainMax
 *                           change->form#submitDebounced">
 *     </div>
 *   </div>
 */

export default class extends Controller {
  static targets = ["min", "max", "minVal", "maxVal", "track", "reset"];
  static values = { min: Number, max: Number, step: Number };

  connect() {
    this.update();
  }

  update() {
    const min = parseFloat(this.minTarget.value);
    const max = parseFloat(this.maxTarget.value);
    const rangeMin = this.minValue;
    const rangeMax = this.maxValue;

    const minPercent = ((min - rangeMin) / (rangeMax - rangeMin)) * 100;
    const maxPercent = ((max - rangeMin) / (rangeMax - rangeMin)) * 100;

    this.trackTarget.style.left = `${minPercent}%`;
    this.trackTarget.style.right = `${100 - maxPercent}%`;

    this.minValTarget.textContent = min.toFixed(1);
    this.maxValTarget.textContent = max.toFixed(1);

    if (this.hasResetTarget) {
      const atDefault = min === this.minValue && max === this.maxValue;
      this.resetTarget.disabled = atDefault;
    }
  }

  reset() {
    this.minTarget.value = this.minValue;
    this.maxTarget.value = this.maxValue;
    this.update();
    this.maxTarget.dispatchEvent(new Event("change", { bubbles: true }));
  }

  clickTrack(event) {
    if (event.target === this.minTarget || event.target === this.maxTarget) return;

    const rect = event.currentTarget.getBoundingClientRect();
    const percent = (event.clientX - rect.left) / rect.width;
    const raw = this.minValue + percent * (this.maxValue - this.minValue);
    const snapped = Math.round(raw / this.stepValue) * this.stepValue;
    const clamped = Math.min(Math.max(snapped, this.minValue), this.maxValue);

    const min = parseFloat(this.minTarget.value);
    const max = parseFloat(this.maxTarget.value);

    if (Math.abs(clamped - min) <= Math.abs(clamped - max)) {
      this.minTarget.value = Math.min(clamped, max - this.stepValue);
      this.constrainMin();
      this.minTarget.dispatchEvent(new Event("change", { bubbles: true }));
    } else {
      this.maxTarget.value = Math.max(clamped, min + this.stepValue);
      this.constrainMax();
      this.maxTarget.dispatchEvent(new Event("change", { bubbles: true }));
    }
  }

  constrainMin() {
    const min = parseFloat(this.minTarget.value);
    const max = parseFloat(this.maxTarget.value);
    if (min >= max) this.minTarget.value = max - this.stepValue;
    this.update();
  }

  constrainMax() {
    const min = parseFloat(this.minTarget.value);
    const max = parseFloat(this.maxTarget.value);
    if (max <= min) this.maxTarget.value = min + this.stepValue;
    this.update();
  }
}
